import React, { useState, useEffect } from 'react';
import './DynamicTelephoneTable.css';

interface TelephoneEntry {
  type: string;
  countryCode: string;
  number: string;
  extension: string;
}

interface DynamicTelephoneTableProps {
  value: { [key: string]: TelephoneEntry };
  onChange: (value: { [key: string]: TelephoneEntry }) => void;
  required?: boolean;
}

const DynamicTelephoneTable: React.FC<DynamicTelephoneTableProps> = ({
  value,
  onChange,
  required = false
}) => {
  const [entries, setEntries] = useState<{ [key: string]: TelephoneEntry }>(value || {});

  useEffect(() => {
    setEntries(value || {});
  }, [value]);

  // Add keyboard support for Ctrl+Enter to add new entry
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        addEntry();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const addEntry = () => {
    const entryCount = Object.keys(entries).length;
    const newKey = `telephone${entryCount + 1}`;
    
    const newEntry: TelephoneEntry = {
      type: '',
      countryCode: '',
      number: '',
      extension: ''
    };
    
    const updatedEntries = { ...entries, [newKey]: newEntry };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (key: string) => {
    const { [key]: removed, ...remainingEntries } = entries;
    
    // Renumber the remaining entries
    const renumberedEntries: { [key: string]: TelephoneEntry } = {};
    const remainingKeys = Object.keys(remainingEntries).sort();
    
    remainingKeys.forEach((oldKey, index) => {
      const newKey = `telephone${index + 1}`;
      renumberedEntries[newKey] = remainingEntries[oldKey];
    });
    
    setEntries(renumberedEntries);
    onChange(renumberedEntries);
  };

  const updateEntry = (key: string, field: keyof TelephoneEntry, value: string) => {
    const updatedEntries = {
      ...entries,
      [key]: { ...entries[key], [field]: value }
    };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const entryKeys = Object.keys(entries).sort();

  return (
    <div className="dynamic-telephone-table">
      <div className="table-header">
        <h4>Telephone Numbers</h4>
        <div className="header-actions">
          <button 
            type="button" 
            className="add-row-btn"
            onClick={addEntry}
            title="Add new telephone entry (Ctrl+Enter)"
          >
            + Add Telephone Number
          </button>
        </div>
      </div>

      {entryKeys.length === 0 ? (
        <div className="empty-state">
          <p>No telephone numbers added yet.</p>
          <button 
            type="button" 
            className="add-first-btn"
            onClick={addEntry}
          >
            Add First Telephone Number
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entryKeys.map((key, index) => (
            <div key={key} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(key)}
                  title="Remove this telephone entry"
                >
                  ×
                </button>
              </div>
              
              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>Type</label>
                    <select
                      value={entries[key].type}
                      onChange={(e) => updateEntry(key, 'type', e.target.value)}
                    >
                      <option value="">Select type</option>
                      <option value="Cellular">Cellular</option>
                      <option value="Business">Business</option>
                      <option value="Landline">Landline</option>
                    </select>
                  </div>
                  
                  <div className="field-group">
                    <label>Country Code</label>
                    <input
                      type="tel"
                      value={entries[key].countryCode}
                      onChange={(e) => updateEntry(key, 'countryCode', e.target.value)}
                      placeholder="e.g., +1"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>Number</label>
                    <input
                      type="tel"
                      value={entries[key].number}
                      onChange={(e) => updateEntry(key, 'number', e.target.value)}
                      placeholder="e.g., 416-555-0123"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>Extension</label>
                    <input
                      type="text"
                      value={entries[key].extension}
                      onChange={(e) => updateEntry(key, 'extension', e.target.value)}
                      placeholder="e.g., 123"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Add another button at the bottom */}
          <div className="table-footer">
            <p className="add-hint">Need to add more telephone numbers?</p>
            <button 
              type="button" 
              className="add-another-btn"
              onClick={addEntry}
            >
              + Add Another Telephone Number
            </button>
          </div>
        </div>
      )}

      {required && entryKeys.length === 0 && (
        <small className="error-text">At least one telephone number is required</small>
      )}
    </div>
  );
};

export default DynamicTelephoneTable; 